import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';

export interface MenuItem {
  label: string;
  icon?: string;
  route?: string;
  url?: string;
  disabled?: boolean;
  visible?: boolean;
  children?: MenuItem[];
  component?: PolymorpheusContent<any>;
  requiredGroups?: string[]; // Groups required to see this menu item
  requireSuperuser?: boolean; // Requires superuser to see this menu item
}

export interface SidebarColorConfig {
  // Background colors
  background?: string;
  submenuBackground?: string;

  // Text colors
  textColor?: string;
  textHoverColor?: string;
  activeTextColor?: string;

  // Background hover/active colors
  hoverBackground?: string;
  activeBackground?: string;
  submenuActiveBackground?: string;

  // Border and accent colors
  borderColor?: string;
  activeBorderColor?: string;

  // Toggle button colors
  toggleColor?: string;
  toggleHoverBackground?: string;

  // Scrollbar colors
  scrollbarThumb?: string;
  scrollbarTrack?: string;
  scrollbarHover?: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
  animations: [
    trigger('slideInOut', [
      state(
        'collapsed',
        style({
          width: '60px',
        })
      ),
      state(
        'expanded',
        style({
          width: '260px',
        })
      ),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '0',
          opacity: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: '1',
        })
      ),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
    trigger('rotateIcon', [
      state(
        'collapsed',
        style({
          transform: 'rotate(0deg)',
        })
      ),
      state(
        'expanded',
        style({
          transform: 'rotate(90deg)',
        })
      ),
      transition('collapsed <=> expanded', animate('200ms ease-in-out')),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '200ms ease-in-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in-out',
          style({ opacity: 0, transform: 'translateY(-10px)' })
        ),
      ]),
    ]),
  ],
})
export class SidebarComponent implements OnChanges {
  @Input() items: MenuItem[] = [];
  @Input() collapsed: boolean = false;
  @Input() colorConfig: SidebarColorConfig = {};
  @Input() autoExpandOnClick: boolean = true; // Enable auto-expand by default
  @Output() collapsedChange = new EventEmitter<boolean>();

  expandedItems: Set<number> = new Set();
  private readonly STORAGE_KEY = 'sidebar-expanded-items';

  // Default color configuration using m4p variables
  private defaultColorConfig: Required<SidebarColorConfig> = {
    background: 'var(--m4p-primary-50)',
    submenuBackground: 'var(--m4p-primary-100)',
    textColor: 'var(--m4p-secondary-700)',
    textHoverColor: 'var(--m4p-secondary-800)',
    activeTextColor: 'var(--m4p-accent-700)',
    hoverBackground: 'var(--m4p-primary-100)',
    activeBackground: 'var(--m4p-accent-100)',
    submenuActiveBackground: 'var(--m4p-accent-200)',
    borderColor: 'var(--m4p-primary-200)',
    activeBorderColor: 'var(--m4p-accent-500)',
    toggleColor: 'var(--m4p-secondary-600)',
    toggleHoverBackground: 'var(--m4p-primary-100)',
    scrollbarThumb: 'var(--m4p-primary-300)',
    scrollbarTrack: 'var(--m4p-primary-50)',
    scrollbarHover: 'var(--m4p-primary-400)',
  };

  constructor(public router: Router) {
    // Load saved expanded items from localStorage
    this.loadExpandedState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && !changes['items'].firstChange) {
      // When menu items change, validate the saved expanded state
      this.validateExpandedState();
    }
  }

  // Getter for merged color configuration
  get colors(): Required<SidebarColorConfig> {
    return { ...this.defaultColorConfig, ...this.colorConfig };
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    // Don't clear expanded items when collapsing the sidebar
    // This preserves the state of expanded submenus
    this.collapsedChange.emit(this.collapsed);
  }

  toggleItem(index: number): void {
    console.log('Toggle item:', index, 'collapsed:', this.collapsed);

    // Toggle submenu state regardless of sidebar collapsed state
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);
    } else {
      this.expandedItems.add(index);
    }

    // Save the expanded state to localStorage
    this.saveExpandedState();
  }

  private saveExpandedState(): void {
    try {
      const expandedArray = Array.from(this.expandedItems);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(expandedArray));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }

  private loadExpandedState(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const expandedArray = JSON.parse(saved) as number[];
        this.expandedItems = new Set(expandedArray);
      }
    } catch (error) {
      console.error('Failed to load sidebar state:', error);
      this.expandedItems = new Set();
    }
  }

  private validateExpandedState(): void {
    // Remove any expanded indices that are out of bounds
    const validIndices = new Set<number>();
    this.expandedItems.forEach(index => {
      if (
        index >= 0 &&
        index < this.items.length &&
        this.items[index]?.children?.length > 0
      ) {
        validIndices.add(index);
      }
    });

    if (validIndices.size !== this.expandedItems.size) {
      this.expandedItems = validIndices;
      this.saveExpandedState();
    }
  }

  isItemExpanded(index: number): boolean {
    return this.expandedItems.has(index);
  }

  navigateToRoute(item: MenuItem): void {
    console.log('Navigate to route:', item.label, 'collapsed:', this.collapsed);

    if (item.disabled || (!item.route && !item.url)) {
      return;
    }

    // Navigate directly - no auto-expand for submenu items
    if (item.route) {
      this.router.navigate([item.route]);
    } else if (item.url) {
      window.open(item.url, '_blank');
    }
  }

  hasVisibleChildren(item: MenuItem): boolean {
    return item.children?.some(child => child.visible !== false) || false;
  }

  isItemVisible(item: MenuItem): boolean {
    return item.visible !== false;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onToggleHover(event: MouseEvent, entering: boolean): void {
    const element = event.target as HTMLElement;
    if (entering) {
      element.style.backgroundColor = this.colors.toggleHoverBackground;
    } else {
      element.style.backgroundColor = '';
    }
  }

  onLinkHover(event: MouseEvent, entering: boolean, isActive: boolean): void {
    const element = event.target as HTMLElement;
    if (entering && !isActive) {
      element.style.backgroundColor = this.colors.hoverBackground;
      element.style.color = this.colors.textHoverColor;
    } else if (!entering && !isActive) {
      element.style.backgroundColor = '';
      element.style.color = this.colors.textColor;
    }
  }

  onSubLinkHover(
    event: MouseEvent,
    entering: boolean,
    isActive: boolean
  ): void {
    const element = event.target as HTMLElement;
    if (entering && !isActive) {
      element.style.backgroundColor = this.colors.hoverBackground;
      element.style.color = this.colors.textHoverColor;
    } else if (!entering && !isActive) {
      element.style.backgroundColor = '';
      element.style.color = this.colors.textColor;
    }
  }

  onIconClick(event: MouseEvent, item: MenuItem, index: number): void {
    console.log('Icon clicked:', item.label, 'collapsed:', this.collapsed);

    // In expanded state, let the main link handle the click
    // In collapsed state, handle the click here
    if (this.collapsed) {
      event.stopPropagation();

      if (item.children && item.children.length > 0) {
        // Handle submenu toggle
        this.toggleItem(index);
      } else {
        // Handle navigation for leaf items
        this.navigateToRoute(item);
      }
    }
    // In expanded state, don't prevent propagation - let the main link handle it
  }

  handleLinkClick(item: MenuItem, index: number): void {
    console.log(
      'Link clicked:',
      item.label,
      'collapsed:',
      this.collapsed,
      'has children:',
      item.children && item.children.length > 0
    );

    if (item.children && item.children.length > 0) {
      // Parent item with children - always toggle submenu
      this.toggleItem(index);
    } else {
      // Leaf item - navigate to route
      this.navigateToRoute(item);
    }
  }

  handleNavigationClick(event: MouseEvent, item: MenuItem): void {
    // Only prevent default behavior on left-clicks
    // Right-clicks should use the browser's default behavior for "Open in new tab"
    if (event.button === 0) {
      // Left click
      event.preventDefault();
      this.navigateToRoute(item);
    }
    // For right-clicks (button === 2), do nothing - let browser handle it
  }

  handleParentNavigationClick(
    event: MouseEvent,
    item: MenuItem,
    index: number
  ): void {
    // Only prevent default behavior on left-clicks
    if (event.button === 0) {
      // Left click
      event.preventDefault();
      this.handleLinkClick(item, index);
    }
    // For right-clicks (button === 2), do nothing - let browser handle it
  }
}
